/**
 * Web authentication startup (spec 15.9, 15.10, 15.22, 15.23).
 *
 * The sequence the spec asks for, and the one implemented here:
 *
 *   browser starts
 *     -> authInitializing (App.tsx holds `loading` true; AuthSplash renders)
 *     -> read persisted session context          (sessionStore.web.ts)
 *     -> restore store / location / user / permissions  (the calls below)
 *     -> authReady
 *     -> evaluate the current web route
 *     -> render the protected screen
 *
 * The thing it deliberately does *not* do is treat `isLoggedIn === false` at
 * startup as "signed out". That flag is false for every browser start, including
 * the ones where the session is perfectly valid, which is exactly the race the
 * spec describes: navigation renders, redirects to the auth stack, and the
 * token turns up afterwards.
 *
 * No new authentication state is introduced (15.10). The restore path dispatches
 * the same thunks and the same `loginState(true)` the PIN screen dispatches, so
 * a restored session and a freshly signed-in one are indistinguishable to every
 * screen downstream.
 */

import React, {useEffect} from 'react';
import {ActivityIndicator, StyleSheet, View} from 'react-native';

import {
  getCountry,
  getCurrentUser,
  getDisplaySetting,
  getPosUserRolesAndPermissions,
  getStoreLocation,
  loginState,
  setCurrentLocation,
  setIndustryDetails,
} from '../../redux/slices/commonSlice';
import {store} from '../../redux/store';
import {webColor} from '../design/webTokens';
import {clearSessionSnapshot, isPageReload, readSessionSnapshot} from './sessionStore';

/**
 * Re-runs the bootstrap the PIN screen runs after a successful sign-in.
 *
 * Split into "must succeed" and "best effort" on purpose (15.12): only the two
 * authenticated calls decide whether the session is still good. A country list
 * or a display-settings call that fails is a data problem, not an
 * authentication one, and must not push the user back to sign-in.
 */
async function restoreSession(
  storeId: number,
  roleId: number,
  locationId?: number,
): Promise<boolean> {
  const dispatch = store.dispatch;

  // Best effort - failures here are logged by their own slices and ignored.
  const optional = Promise.allSettled([
    dispatch(getDisplaySetting({storeId})),
    dispatch(getCountry(true)),
    dispatch(getStoreLocation({storeId}))
      .unwrap()
      .then(response => {
        // The location the cashier had selected, if it still exists;
        // otherwise the first one, as a fresh sign-in would pick.
        const locations = response?.storeLocation ?? [];
        const location =
          locations.find((item: {id?: number}) => item?.id === locationId) ?? locations[0];
        if (location) {
          dispatch(
            setCurrentLocation({
              id: location.id,
              name: location.locationName,
              timeZone: location.timeZone,
            }),
          );
        }
      }),
  ]);

  try {
    /*
     * These two are the session test. Permissions decide what the drawer and
     * every screen may show, and the current user supplies the industry the
     * theme keys off - a session without either is not usable, and a server
     * that refuses them is telling us the session is over.
     */
    const [, currentUserResponse] = await Promise.all([
      dispatch(getPosUserRolesAndPermissions({storeId, id: roleId})).unwrap(),
      dispatch(getCurrentUser()).unwrap(),
    ]);

    const industry = currentUserResponse?.currentUser?.industry?.name;
    if (industry) {
      dispatch(setIndustryDetails({name: industry}));
    }
  } catch {
    return false;
  }

  await optional;
  dispatch(loginState(true));
  return true;
}

export async function resolveInitialAuth(): Promise<boolean> {
  /*
   * Only a refresh puts the session back without the PIN. Any other start -
   * the tab or browser was closed and reopened, the address typed again, a
   * bookmark, a new tab, Electron relaunched - falls through to App.tsx's
   * existing check, which shows the PIN screen while a token and email are
   * stored. The snapshot is dropped too: otherwise pressing F5 on that PIN
   * screen would count as a refresh and skip the PIN after all.
   */
  if (!isPageReload()) {
    clearSessionSnapshot();
    return false;
  }

  const snapshot = readSessionSnapshot();
  if (!snapshot) {
    return false;
  }

  let restored = false;
  try {
    restored = await restoreSession(snapshot.storeId, snapshot.roleId, snapshot.locationId);
  } catch {
    /*
     * restoreSession already turns a refused session into `false`; this
     * catches anything unexpected (a thunk throwing synchronously, storage
     * errors). App.tsx has no catch of its own, so letting it escape would
     * leave `loading` true and the user parked on the splash for good.
     */
    restored = false;
  }

  if (!restored) {
    /*
     * 15.23: drop the context that just failed so the next start goes straight
     * to sign-in instead of retrying a dead session on every reload. Nothing
     * else in Redux is touched - an unrelated slice has no business being
     * cleared because authentication expired.
     */
    clearSessionSnapshot();
  }

  return restored;
}

/** Static startup splash painted by public/index.html. */
const BOOT_SPLASH_ID = 'zb-boot-splash';

/**
 * Shown while authentication is being restored (15.22).
 *
 * The loading branch used to render nothing, which on native is invisible - the
 * check is one storage read. On web it spans several network round trips, and a
 * blank white page for two seconds reads as a broken app.
 *
 * On the first start the ZenBasket POS logo splash from index.html sits on top
 * of this, covering the gap from the first byte of HTML through bundle download
 * and session restore. It is removed when this component unmounts - i.e. when
 * App.tsx clears `loading` because initialization really finished - so its
 * lifetime is the real auth state, never a timer, and the sign-in screen can
 * never flash underneath it. Later mounts (sign-out) find it gone and show the
 * spinner below as before.
 */
export const AuthSplash = (): React.JSX.Element => {
  useEffect(() => () => document.getElementById(BOOT_SPLASH_ID)?.remove(), []);

  return (
    <View style={styles.splash}>
      <ActivityIndicator size="large" color={webColor.primary} />
    </View>
  );
};

const styles = StyleSheet.create({
  splash: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
